import { useState } from 'react';

interface TagModalProps {
  open: boolean;
  title: string;
  onClose: () => void;
  onTag: (handles: string[]) => void;
}

const SUGGESTED_PEOPLE: { name: string; handle: string }[] = [
  { name: 'Joachim Migos', handle: '@joachinmigos' },
  { name: 'Mic Flammez', handle: '@micflammez' },
  { name: 'Sethlo', handle: '@sethloofficiel' },
  { name: 'Ralycia', handle: '@ralyciaofficiel' },
  { name: 'Pikaluz', handle: '@pikaluz' },
  { name: 'Laura Bazooka', handle: '@laurabazooka' },
  { name: 'Anodaboy', handle: '@anodaboy' },
  { name: 'Toofan', handle: '@toofanofficiel' },
  { name: 'Santrinos Raphael', handle: '@santrinosraphael' },
  { name: 'Kiko Golden Boy', handle: '@kikogoldenboy' },
  { name: 'Jeandatol', handle: '@jeandatol_' },
  { name: 'Bhadie Kelly', handle: '@bhadie.kellyy' },
  { name: 'Hodako TV', handle: '@hodakotv2288' },
  { name: 'TV7', handle: '@donnetv7' },
  { name: 'Emmanuel Adebayor', handle: '@e_adebayor' },
  { name: 'Voklia Jokes', handle: '@vokliajokes' },
];

function TagModal({ open, title, onClose, onTag }: TagModalProps) {
  const [selected, setSelected] = useState<string[]>([]);
  const [input, setInput] = useState('');
  const [error, setError] = useState('');

  if (!open) return null;

  const toggle = (handle: string) => {
    setSelected((prev) =>
      prev.includes(handle) ? prev.filter((h) => h !== handle) : [...prev, handle],
    );
    setError('');
  };

  const addCustom = () => {
    const h = input.trim();
    if (!h) return;
    const withAt = h.startsWith('@') ? h : `@${h}`;
    if (!selected.includes(withAt)) {
      setSelected((prev) => [...prev, withAt]);
    }
    setInput('');
    setError('');
  };

  const handleSubmit = () => {
    if (selected.length === 0) {
      setError('Choisissez au moins une personne à mentionner.');
      return;
    }
    onTag(selected);
    setSelected([]);
    setInput('');
    setError('');
  };

  const handleClose = () => {
    setSelected([]);
    setInput('');
    setError('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-background-950/70 backdrop-blur-sm"
        onClick={handleClose}
      />

      <div className="relative w-full max-w-md bg-background-50 border border-background-200/70 rounded-xl overflow-hidden max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-200/70 shrink-0">
          <div className="flex items-center gap-2">
            <i className="ri-at-line text-accent-400 text-xl" />
            <h3 className="font-heading font-semibold text-lg text-foreground-50">
              Mentionner des personnes
            </h3>
          </div>
          <button
            onClick={handleClose}
            className="w-9 h-9 flex items-center justify-center text-foreground-500 hover:text-foreground-50 transition-colors"
            aria-label="Fermer"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          <p className="text-sm text-foreground-400">
            Faites découvrir cette vidéo à vos amis en les mentionnant :
          </p>
          <p className="text-sm font-semibold text-foreground-50 line-clamp-2">{title}</p>

          {/* Champ personnalisé */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <i className="ri-at-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500" />
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addCustom();
                  }
                }}
                placeholder="Ajouter un pseudo…"
                className="w-full pl-9 pr-3 py-2.5 rounded-md bg-background-100 border border-background-300/60 text-sm text-foreground-50 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
              />
            </div>
            <button
              onClick={addCustom}
              className="px-3 py-2 rounded-md bg-background-100 text-foreground-50 text-sm font-medium hover:bg-background-200/60 transition-colors whitespace-nowrap"
            >
              Ajouter
            </button>
          </div>

          {/* Suggestions */}
          <div>
            <p className="text-xs font-medium text-foreground-400 uppercase tracking-wider mb-2">
              Suggestions
            </p>
            <div className="flex flex-wrap gap-2">
              {SUGGESTED_PEOPLE.map((p) => {
                const isSelected = selected.includes(p.handle);
                return (
                  <button
                    key={p.handle}
                    onClick={() => toggle(p.handle)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                      isSelected
                        ? 'bg-accent-500 text-foreground-950'
                        : 'bg-background-100 text-foreground-400 hover:text-foreground-50 border border-background-300/60'
                    }`}
                  >
                    {p.handle}
                    {isSelected && <i className="ri-check-line" />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Sélection */}
          {selected.length > 0 && (
            <div className="p-3 rounded-lg bg-background-100 border border-background-200/70">
              <p className="text-xs text-foreground-400 mb-2">
                {selected.length} personne(s) mentionnée(s) :
              </p>
              <div className="flex flex-wrap gap-1.5">
                {selected.map((h) => (
                  <span
                    key={h}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-500/10 text-primary-400 text-xs font-medium"
                  >
                    {h}
                  </span>
                ))}
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-accent-100/10 border border-accent-400/20">
              <i className="ri-error-warning-line text-accent-400 mt-0.5 shrink-0" />
              <p className="text-sm text-accent-400">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-3 px-5 py-4 border-t border-background-200/70 shrink-0">
          <button
            onClick={handleClose}
            className="flex-1 px-4 py-2.5 rounded-md bg-background-100 text-foreground-400 text-sm font-medium hover:text-foreground-50 transition-colors whitespace-nowrap"
          >
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            className="flex-1 px-4 py-2.5 rounded-md bg-primary-500 text-foreground-950 font-semibold text-sm hover:bg-primary-400 transition-colors whitespace-nowrap inline-flex items-center justify-center gap-2"
          >
            <i className="ri-at-line" />
            Mentionner
          </button>
        </div>
      </div>
    </div>
  );
}

export default TagModal;